import { Link } from "react-router-dom";
import TopBar from "@/components/feature/TopBar";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { type ArrondissementData } from "@/mocks/arrondissements";
import { PHONE } from "@/mocks/updiag";
import {
  schemaLocalBusiness,
  schemaBreadcrumb,
  renderSchema,
} from "@/lib/schemas";

interface Props {
  data: ArrondissementData;
}

export default function ArrondissementPage({ data }: Props) {
  const breadcrumbItems = [
    { name: "Accueil", url: "/" },
    { name: "Zones", url: "/zones" },
    { name: "Paris", url: "/zones/paris-75" },
    { name: data.nom, url: `/paris/${data.slug}` },
  ];

  return (
    <>
      <title>{data.metaTitle}</title>
      <meta name="description" content={data.metaDesc} />
      <link rel="canonical" href={`https://updiag.fr/paris/${data.slug}`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(schemaLocalBusiness())}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={renderSchema(schemaBreadcrumb(breadcrumbItems))}
      />

      <TopBar />
      <Header />

      <main>
        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={data.heroImage}
              alt={`Diagnostic immobilier ${data.nom}`}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-[#0F3B5F]/82"></div>
          </div>

          <div className="relative max-w-5xl mx-auto px-6">
            <nav className="flex flex-wrap items-center gap-2 mb-5 text-white/70 text-sm">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>›</span>
              <Link to="/zones" className="hover:text-white">Zones</Link>
              <span>›</span>
              <Link to="/zones/paris-75" className="hover:text-white">Paris</Link>
              <span>›</span>
              <span className="text-white">{data.nom}</span>
            </nav>

            <div className="inline-flex items-center gap-2 bg-[#F97316] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
              Paris · Arrondissements {data.arrs}
            </div>

            <h1
              className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight"
              style={{ fontFamily: "Barlow, sans-serif" }}
            >
              {data.h1}
            </h1>
            <p className="text-lg text-white/80 mb-8 max-w-3xl">{data.intro}</p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/devis"
                className="flex items-center justify-center gap-2 px-7 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i> Obtenir mon devis
              </Link>
              <a
                href={`tel:${PHONE.replace(/\s/g, "")}`}
                className="flex items-center justify-center gap-2 px-7 py-4 border-2 border-white text-white rounded-xl font-bold hover:bg-white/10 transition-all whitespace-nowrap"
              >
                <i className="ri-phone-fill"></i> {PHONE}
              </a>
            </div>
          </div>
        </section>

        {/* Bâti local */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-wider text-[#F97316] mb-3">
                Parc immobilier local
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F] mb-6"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Le bâti de {data.nom}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">{data.bati}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 p-6 bg-gray-50 rounded-2xl">
              <div className="text-center">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Prix au m²</div>
                <div className="font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                  {data.prixM2}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Arrondissements</div>
                <div className="font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                  {data.arrs}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Intervention</div>
                <div className="font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                  Sous 48h
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">DPE</div>
                <div className="font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                  Dès 130 €
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-[#0F3B5F] mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Diagnostics les plus demandés ({data.arrs})
            </h3>
            <ul className="space-y-3 mb-12">
              {data.diagnostics.map((d, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-green-700 text-sm"></i>
                  </span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-xl font-bold text-[#0F3B5F] mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Quartiers couverts
            </h3>
            <div className="flex flex-wrap gap-2 mb-8">
              {data.quartiers.map((q, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm text-gray-700"
                >
                  <i className="ri-map-pin-line text-[#F97316]"></i>
                  <strong>{q}</strong>
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-wider text-[#F97316] mb-3">
                Services disponibles
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F]"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Tous nos diagnostics dans le {data.arrs}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { slug: "dpe", name: "DPE", tag: "Diagnostic énergétique", price: "130 €", icon: "ri-fire-line", featured: true },
                { slug: "pack-vente", name: "Pack Vente", tag: "DDT complet", price: "280 €", icon: "ri-home-2-line" },
                { slug: "pack-location", name: "Pack Location", tag: "Pour bailleurs", price: "210 €", icon: "ri-key-line" },
                { slug: "plomb", name: "CREP Plomb", tag: "Biens avant 1949", price: "110 €", icon: "ri-drop-line" },
              ].map((s) => (
                <Link
                  key={s.slug}
                  to={`/diagnostics/${s.slug}`}
                  className={`bg-white rounded-2xl p-6 transition-all hover:-translate-y-1 hover:shadow-xl ${
                    s.featured ? "border-2 border-[#F59E0B] bg-amber-50" : "border border-gray-100"
                  }`}
                >
                  {s.featured && (
                    <span className="inline-block bg-[#F59E0B] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                      ★ Service phare
                    </span>
                  )}
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl mb-4">
                    <i className={`${s.icon} text-2xl text-[#0F3B5F]`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>
                    {s.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">{s.tag}</p>
                  <div className="text-sm text-gray-400 mb-1">Dès</div>
                  <div className="text-2xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                    {s.price}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
